import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SCENARIOS, SCENARIO_ORDER } from '@/seed/scenarios';
import { PERSONAS_SEED } from '@/seed/personas';
import { readTemperature, applyDelta } from '@/lib/roleplay/temperature';
import { composeHint, type HintPayload } from '@/lib/roleplay/hint';
import {
  initDirectorState,
  reduce as reduceDirector,
  composeDirective,
  type DirectorState,
} from '@/lib/roleplay/director';
import { DIRECTOR_CONFIG } from '@/seed/director';
import { gradeSession } from '@/lib/scoring/gradeSession';
import { useLearner, useLearnerStore } from '@/state/learnerStore';
import { AVATAR_IDS } from '@/constants/avatars';
import { COACH } from '@/constants/coach';
import { getClassifier } from '@/lib/grading/classifier';
import { runClassifierDispatch } from '@/lib/classifier/dispatch';
import { appHref } from '@/lib/nav';
import type { MilestoneEvent, MilestoneTag } from '@/lib/grading/types';
import AvatarMount, { type AvatarHandle } from '@/components/avatar/AvatarMount';
import StartSessionModal from '@/components/StartSessionModal';
import MicButton from '@/components/mic/MicButton';
import { useAvatarMic, type MicState } from '@/lib/mic/useAvatarMic';
import { buildRoleplayStartData } from '@/lib/codebaby/startData';
import type { CBClientData, CBResponse } from '@/lib/codebaby/types';
import type { PersonaKey } from '@/constants/avatars';
import type { ScenarioConfig } from '@/types/scenario';
import type { SessionRecord, TranscriptTurn } from '@/types/session';
import './RoleplayScreen.css';

export default function RoleplayScreen() {
  const { scenarioId } = useParams();
  const scenario = scenarioId ? SCENARIOS[scenarioId] : undefined;
  if (!scenario) return <ScenarioMissing scenarioId={scenarioId} />;
  return <RoleplayInterior scenario={scenario} />;
}

function ScenarioMissing({ scenarioId }: { scenarioId: string | undefined }) {
  return (
    <div className="roleplay-missing">
      <p className="eyebrow">404</p>
      <h1>No scenario with id {scenarioId ?? '—'}.</h1>
      <p>
        {/* Plain anchor — Coach↔persona transitions force a full page load
            because CodeBaby's loader can't be re-initialized on the same
            page (see reference_codebaby_integration_pitfalls memory). */}
        <a href={appHref('/')}>Back to dashboard</a>
      </p>
    </div>
  );
}

/* ----------------------------- Interior ------------------------------- */

type SceneState = 'waiting' | 'speaking' | 'you-speaking';

function RoleplayInterior({ scenario }: { scenario: ScenarioConfig }) {
  const persona = PERSONAS_SEED[scenario.persona_id];
  const learner = useLearner();
  const completeSession = useLearnerStore((s) => s.completeSession);

  // Avatar wiring
  const personaRef = useRef<AvatarHandle>(null);
  const [avatarReady, setAvatarReady] = useState(false);
  const [personaSpeaking, setPersonaSpeaking] = useState(false);

  // Session state
  const [started, setStarted] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([]);
  const [inputText, setInputText] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [hintOpen, setHintOpen] = useState(false);
  const [activeHint, setActiveHint] = useState<HintPayload | null>(null);
  const [temperature, setTemperature] = useState(scenario.start_temperature);
  const [clientDataHistory, setClientDataHistory] = useState<CBClientData[]>([]);

  // Per-turn classifier dispatch — replaces the Comcast meta-avatar socket
  // (ADR 0002). `getClassifier()` resolves to mock or live based on
  // loaderMode(); we capture once per session so the implementation
  // doesn't swap mid-roleplay. `lastProducerTextRef` is the producer's
  // most recent turn, captured the moment they hit Send or mic-final
  // (state reads inside onAvatarResponse would be stale). `alreadyHitRef`
  // accumulates milestone tags as the classifier reports them so the
  // next call's prompt context matches what's been credited.
  // useMemo (not useRef(getClassifier())) — useRef evaluates its initial-value
  // expression on every render even though it only stores the first. With the
  // factory logging on construction that produces hundreds of redundant
  // [classifier] lines per session and unnecessary classifier instances.
  const classifierRef = useRef(useMemo(() => getClassifier(), []));
  const lastProducerTextRef = useRef<string>('');
  const alreadyHitRef = useRef<Set<MilestoneTag>>(new Set());
  // Authoritative, race-proof record of every milestone the classifier
  // credited this session. Appended synchronously the moment a classification
  // lands (before the async clientDataHistory replace-last setState), so it
  // can't be scrambled by out-of-order arrivals or lost to an aborted dispatch.
  // gradeSession() grades milestones_hit/score/outcome off THIS, not the
  // best-effort clientDataHistory. See gradeSession.ts.
  const creditedRef = useRef<MilestoneEvent[]>([]);
  const classifierAbortRef = useRef<AbortController | null>(null);
  // Conversation Director — accumulates state across turns and steers the live
  // avatar so the roleplay progresses + stays realistic (see lib/roleplay/director).
  // `elapsedRef` mirrors the `elapsed` state so the async classifier callback
  // and the rep-turn handlers read the CURRENT clock, not a stale closure value.
  const directorRef = useRef<DirectorState>(initDirectorState());
  const elapsedRef = useRef(0);

  // Stall recovery (mic path). The loader auto-asks the final mic transcript;
  // occasionally that auto-ask is dropped and the persona never replies, yet
  // the avatar is healthy — typing the same line makes her respond, because
  // typing calls ask() directly. So when a spoken turn finalizes and NO
  // response arrives within the window, we re-issue it via ask() ourselves.
  // The window is deliberately generous so we only fire on a genuine stall,
  // not a slow-but-working round trip (which would double-send).
  const STALL_RECOVERY_MS = 9000;
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMicTurnRef = useRef<string | null>(null);
  const personaSpeakingRef = useRef(false);
  const disarmStallWatchdog = () => {
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
    pendingMicTurnRef.current = null;
  };
  const armStallWatchdog = (text: string) => {
    disarmStallWatchdog();
    pendingMicTurnRef.current = text;
    stallTimerRef.current = setTimeout(() => {
      const pending = pendingMicTurnRef.current;
      pendingMicTurnRef.current = null;
      stallTimerRef.current = null;
      // Only recover if she's still silent (not mid-response) and we have the
      // turn text. Fail-safe: a recovery bug must not break the conversation.
      if (!pending || personaSpeakingRef.current) return;
      try {
        console.warn('[stall] no response to spoken turn; re-issuing via ask()');
        personaRef.current?.ask(pending);
      } catch (err) {
        console.warn('[stall] recovery ask failed (non-fatal):', err);
      }
    }, STALL_RECOVERY_MS);
  };

  // Fold a turn into the director state, then inject the resulting directive
  // into the live avatar so it conditions the persona's NEXT response. Called
  // at rep-turn time (cue detection on the rep's words — immediate steering)
  // and again once the classifier returns (to fold in the milestone read).
  const steerDirector = (
    producerText: string,
    signal?: import('@/lib/codebaby').CBPersonaSignals,
    personaText?: string | null,
  ) => {
    // Fail-safe: the director is an enhancement layer — a bug in it must never
    // break the live conversation, so swallow + log rather than throw upward.
    try {
      directorRef.current = reduceDirector(
        directorRef.current,
        {
          producerText,
          personaText: personaText ?? null,
          milestoneTag: signal?.milestone_tag ?? null,
          objectionLabel: signal?.objection_raised ?? null,
          yieldQuality: null,
          isKeyMoment: Boolean(signal?.is_key_moment),
        },
        DIRECTOR_CONFIG,
      );
      const directive = composeDirective(directorRef.current, elapsedRef.current, DIRECTOR_CONFIG);
      // Runtime context channel — works even on avatars whose base prompt we
      // can't edit (the directive is natural-language imperative).
      personaRef.current?.notifyContext({ type: 'director_guidance', description: directive });
    } catch (err) {
      console.warn('[director] steer failed (non-fatal):', err);
    }
  };
  useEffect(
    () => () => {
      classifierAbortRef.current?.abort();
      disarmStallWatchdog();
    },
    [],
  );

  const startData = useMemo(
    () => buildRoleplayStartData(learner, scenario.persona_id),
    [learner, scenario.persona_id],
  );

  // Conversation mode: the portal runs a continuous (Whisper/diarized)
  // listener that stays open across turns. Flip on per deploy via
  // VITE_CONVERSATION_MIC=true. Turn-based avatars leave it unset → the
  // classic tap-per-turn behavior is unchanged.
  const conversationMic = import.meta.env.VITE_CONVERSATION_MIC === 'true';

  // Mic plumbing — `useAvatarMic` bundles the controller, the
  // pre-prompt/recovery overlays, and the avatar-mount subscription
  // wiring. Host only handles transcript display via `onFinalText`.
  const mic = useAvatarMic({
    continuous: conversationMic,
    toggle: () => {
      // If the persona is mid-segment when the user reaches for the mic,
      // pause so their voice doesn't overlap the avatar's TTS. (Audio
      // ducking on barge-in is a stretch goal — pause is enough for V1.)
      if (personaSpeaking) {
        personaRef.current?.pause();
      }
      personaRef.current?.microphoneToggle();
    },
    sessionReady: avatarReady,
    disabled: !started || hintOpen,
    onInterimText: (text) => setInputText(text),
    onFinalText: (text) => {
      // Display only — the loader auto-asks on mic-final
      // (conversationConnection.js:200). Calling ask() here would double-send.
      setTranscript((t) => [...t, { speaker: 'learner', text, at_s: elapsed }]);
      lastProducerTextRef.current = text;
      // Mic-final: the loader auto-asks this text, so steer immediately so the
      // directive is in place before the persona generates its reply.
      steerDirector(text);
      // Arm stall recovery: if the loader's auto-ask is dropped and no response
      // comes back, we re-issue this turn via ask() (see armStallWatchdog).
      armStallWatchdog(text);
      setInputText('');
    },
  });

  // Session clock — runs while started AND not paused for a hint.
  useEffect(() => {
    if (!started || hintOpen) return;
    const id = window.setInterval(
      () =>
        setElapsed((s) => {
          elapsedRef.current = s + 1;
          return s + 1;
        }),
      1000,
    );
    return () => window.clearInterval(id);
  }, [started, hintOpen]);

  // Hint open or session ended → release the mic if it's hot. Without this
  // the user can leave a session with the listening indicator still pulsing.
  useEffect(() => {
    if ((hintOpen || !started) && mic.state === 'listening') {
      personaRef.current?.microphoneToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintOpen, started]);

  const scenarioIndex = SCENARIO_ORDER.indexOf(scenario.scenario_id);
  const scenarioNumber = scenarioIndex >= 0 ? scenarioIndex + 1 : 1;

  const tempView = useMemo(() => readTemperature(temperature), [temperature]);

  const sceneState: SceneState = hintOpen
    ? 'waiting'
    : personaSpeaking
      ? 'speaking'
      : mic.state === 'listening'
        ? 'you-speaking'
        : 'waiting';

  /* -------------------------- Avatar callbacks ------------------------ */

  const onAvatarReady = () => setAvatarReady(true);

  const onAvatarResponse = (r: CBResponse) => {
    // A real response landed — the turn wasn't stalled; cancel any recovery.
    disarmStallWatchdog();
    personaSpeakingRef.current = true;
    setPersonaSpeaking(true);
    setTranscript((t) => {
      // Some persona dialog flows emit two responses for one ask. The
      // CodeBaby loader's TTS plays the LATEST one out loud, so we
      // overwrite the prior persona turn here to keep the visible
      // transcript aligned with the audio. See CBResponse.supersedesPrevious.
      if (r.supersedesPrevious && t.length > 0 && t[t.length - 1]!.speaker === 'persona') {
        const next = t.slice(0, -1);
        next.push({ speaker: 'persona', text: r.answerText, at_s: elapsed });
        return next;
      }
      return [...t, { speaker: 'persona', text: r.answerText, at_s: elapsed }];
    });
    // In mock mode, clientData arrives WITH the response (the mock brain
    // fabricates it inline). In live mode, the persona's primary doesn't
    // emit clientData — it arrives separately on `onPersonaSignals` once
    // the persona-meta avatar finishes classifying. We still apply whatever
    // came on the response (neutralClientData in live, real data in mock)
    // so mock keeps working unchanged; live's signal merges in below.
    // Skip applying clientData on a supersede — the prior turn's signal
    // already fired and we don't want to double-count temperature deltas.
    if (!r.supersedesPrevious) {
      setTemperature((v) => applyDelta(v, r.clientData.temperature_delta));
      setClientDataHistory((h) => [...h, r.clientData]);
      // Kick off the classifier with the producer's last turn + this
      // persona reply. Skip on the opener (no producer turn yet) and
      // on supersede (the prior turn's classification still stands).
      const producerText = lastProducerTextRef.current;
      if (producerText) {
        // Fold the persona's OWN reply into the director right away (no need to
        // wait on the classifier): if the buyer just accepted ("fine, set it
        // up / send me an agenda"), commitment locks now, so the next directive
        // forbids walking it back — independent of how the classifier scored
        // the rep's (often hedged) ask.
        steerDirector(producerText, undefined, r.answerText);
        classifierAbortRef.current?.abort();
        const controller = new AbortController();
        classifierAbortRef.current = controller;
        void runClassifierDispatch({
          classifier: classifierRef.current,
          personaKey: scenario.persona_id,
          producerText,
          personaText: r.answerText,
          alreadyHit: Array.from(alreadyHitRef.current),
          turnId: `t-${clientDataHistory.length}`,
          turnIndex: clientDataHistory.length,
          signal: controller.signal,
          onSignals: (s) => {
            if (s.milestone_tag) {
              const tag = s.milestone_tag as MilestoneTag;
              // First time this tag is credited, record it on the authoritative
              // stream the grader reads. `producerText` is the turn captured at
              // dispatch time (closure), so it stays correctly paired even if a
              // later turn's classification lands first.
              if (!alreadyHitRef.current.has(tag)) {
                creditedRef.current.push({
                  tag,
                  turnId: `t-${clientDataHistory.length}`,
                  producerText,
                  isKeyMoment: Boolean(s.is_key_moment),
                  momentType: s.moment_type ?? null,
                  ...(s.moment_summary ? { notes: s.moment_summary } : {}),
                });
              }
              alreadyHitRef.current.add(tag);
            }
            onPersonaSignals(s);
            // Fold the classifier's read into the director (committed/phase/
            // settled), refreshing the steering context for the next turn.
            steerDirector(producerText, s, r.answerText);
          },
          onError: (err) => {
            console.warn('[classifier] dispatch failed:', err.message);
          },
        });
      }
    }
  };

  const onPersonaSignals = (s: import('@/lib/codebaby').CBPersonaSignals) => {
    // Live-mode signals from the persona-meta socket. Apply temperature and
    // REPLACE the last clientDataHistory entry (which was the neutral
    // placeholder pushed by onAvatarResponse) with the LLM-classified
    // version. This keeps the history 1:1 with persona turns so the grader
    // can pair clientData[N] with learnerTurns[N] cleanly. Mock mode never
    // fires this — it embeds clientData directly in the response and we
    // append once.
    setTemperature((v) => applyDelta(v, s.temperature_delta));
    setClientDataHistory((h) => {
      const next: typeof h = [
        ...h.slice(0, -1),
        { ...s, persona_state: 'speaking', coach_note_id: null },
      ];
      return h.length === 0 ? [{ ...s, persona_state: 'speaking', coach_note_id: null }] : next;
    });
  };

  const onSegmentEnded = () => {
    personaSpeakingRef.current = false;
    setPersonaSpeaking(false);
  };

  /* -------------------------- Learner actions ------------------------ */

  const submitLearnerTurn = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setTranscript((t) => [...t, { speaker: 'learner', text: trimmed, at_s: elapsed }]);
    lastProducerTextRef.current = trimmed;
    // A fresh turn supersedes any pending mic-stall recovery.
    disarmStallWatchdog();
    // Steer BEFORE asking so the directive (incl. cues detected in this very
    // turn — scheduling asks, credibility) conditions the persona's response.
    steerDirector(trimmed);
    personaRef.current?.ask(trimmed);
  };

  const onSend = () => {
    submitLearnerTurn(inputText);
    setInputText('');
  };

  const onStartSession = () => {
    setStarted(true);
    // Fresh director state + clock for a clean run (covers in-place restart).
    directorRef.current = initDirectorState();
    elapsedRef.current = 0;
    setElapsed(0);
    disarmStallWatchdog();
    // Opening line is a scripted utterance owned by the scenario seed, played
    // via `playResponse()` (never via the dialog flow — see the single-source
    // rule in the avatar-content feedback memory). In live mode, the persona's
    // CodeBaby dialog flow MUST be configured to not auto-open on mount.
    const opener = scenario.opening_line;
    personaRef.current?.playResponse(opener);
    setTranscript([{ speaker: 'persona', text: opener, at_s: 0 }]);
  };

  const onAskHint = () => {
    // Freeze persona audio + animation so the hint card is the clear focus.
    // `pause()` vs `mute()` — Linda stops (frame + audio), not just silenced.
    personaRef.current?.pause();
    // Don't let a stall recovery fire into a paused session.
    disarmStallWatchdog();
    // Compose the hint from the live session signals so it reads like Coach
    // is actually paying attention, not reciting canned advice.
    setActiveHint(
      composeHint({
        scenario,
        transcript,
        clientDataHistory,
        temperature,
      }),
    );
    setHintOpen(true);
  };

  const onResumeFromHint = () => {
    setHintOpen(false);
    setActiveHint(null);
    personaRef.current?.play();
  };

  // Build the SessionRecord from current state — shared between End session
  // (goes to debrief) and Exit (goes to dashboard). Both code paths must
  // persist so Coach's dashboard greeting accurately reflects the most
  // recent thing the learner did.
  const buildSessionRecord = (): SessionRecord => {
    // Pass clientDataHistory so gradeSession can prefer the persona meta
    // socket's LLM-authored per-turn classifications (is_key_moment,
    // moment_type, moment_summary) over keyword regex. This is what makes
    // Coach's debrief reflect the actual conversation rather than generic
    // boilerplate keyed off whichever phrases happened to match.
    // creditedRef is the authoritative milestone stream (race-proof); the live
    // `temperature` state is the gauge the learner actually watched. Both beat
    // re-deriving from the best-effort clientDataHistory. See gradeSession.ts.
    const verdict = gradeSession(
      transcript,
      scenario,
      clientDataHistory,
      creditedRef.current,
      Math.max(0, Math.min(4, temperature)),
    );
    const priorAttempts = learner.session_history.filter(
      (s) => s.persona_id === scenario.persona_id,
    ).length;
    const startedAt = new Date(Date.now() - elapsed * 1000).toISOString();
    return {
      id: `sess-${scenario.persona_id}-${Date.now()}`,
      persona_id: scenario.persona_id,
      attempt_number: priorAttempts + 1,
      started_at: startedAt,
      duration_s: elapsed,
      outcome: verdict.outcome,
      score: verdict.score,
      milestones_hit: verdict.milestones_hit,
      transcript,
      moments: verdict.moments,
    };
  };

  const onEndSession = () => {
    const record = buildSessionRecord();
    completeSession(record);
    // Hard nav back to a Coach-route — see Coach↔persona note above.
    // appHref() prepends the deploy base (/real-estate) — a raw '/debrief/...'
    // drops the prefix and white-screens under path routing.
    window.location.href = appHref(`/debrief/${record.id}`);
  };

  // Exit handler — only persists if the learner actually engaged. Without
  // this guard, clicking Start then immediately Exit would log an empty
  // attempt that pollutes session_history.
  const onExit = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (started && transcript.length > 0) {
      const record = buildSessionRecord();
      completeSession(record);
    }
    window.location.href = appHref('/');
  };

  return (
    <div className={`roleplay roleplay--${persona.color_key}`} data-paused={hintOpen || undefined}>
      {!started && (
        <StartSessionModal
          eyebrow={`Scenario ${scenarioNumber} of ${SCENARIO_ORDER.length} · ${scenario.short_label}`}
          headline={`You're in a meeting with ${persona.display_name}.`}
          body={`You have 20 minutes. ${persona.pronouns.subject[0]!.toUpperCase()}${persona.pronouns.subject.slice(1)}'ll open — react like you're in the real room. Tap the mic or type your response.`}
          ctaLabel="Start session"
          ready={avatarReady}
          onStart={onStartSession}
        />
      )}
      <ScenarioTopBar
        onExit={onExit}
        scenario={scenario}
        scenarioNumber={scenarioNumber}
        totalScenarios={SCENARIO_ORDER.length}
        elapsed={elapsed}
        onEndSession={onEndSession}
      />
      <BriefStrip scenario={scenario} />
      <TemperatureGauge
        fraction={tempView.fraction}
        label={tempView.label}
        colorKey={persona.color_key}
        personaName={persona.display_name.split(' ')[0]!}
        pronouns={persona.pronouns}
      />
      <div className="roleplay__stage">
        <PersonaStage
          ref={personaRef}
          scenario={scenario}
          sceneState={sceneState}
          colorKey={persona.color_key}
          startData={startData}
          onReady={onAvatarReady}
          onResponse={onAvatarResponse}
          onSegmentEnded={onSegmentEnded}
          micProps={mic.avatarProps}
          onPersonaSignals={onPersonaSignals}
        />
        <TranscriptRail
          transcript={transcript}
          personaName={persona.display_name}
          sceneState={sceneState}
        />
      </div>
      <ControlsRow
        value={inputText}
        onChange={setInputText}
        onSend={onSend}
        onMicTap={mic.onTap}
        micState={mic.state}
        continuous={conversationMic}
        disabled={!started || hintOpen}
      />
      <SecondaryRow onAskHint={onAskHint} hintPrimed={started && !hintOpen} />
      {hintOpen && activeHint && (
        <PausedHintCard
          hint={activeHint}
          personaName={persona.display_name}
          onResume={onResumeFromHint}
        />
      )}
      {mic.overlays}
    </div>
  );
}

/* --------------------------- Scenario top bar ------------------------- */

function ScenarioTopBar({
  scenario,
  scenarioNumber,
  totalScenarios,
  elapsed,
  onExit,
  onEndSession,
}: {
  scenario: ScenarioConfig;
  scenarioNumber: number;
  totalScenarios: number;
  elapsed: number;
  onExit: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onEndSession: () => void;
}) {
  return (
    <header className="scenario-topbar">
      {/* Coach↔persona transitions force a full page load. The handler also
          persists the in-progress session so Coach's dashboard greeting
          reflects this attempt — even if the learner exits without going
          through the debrief. */}
      <a href={appHref('/')} className="scenario-topbar__exit" onClick={onExit}>
        <ChevronLeft /> Exit
      </a>
      <div className="scenario-topbar__badge">
        <span className="scenario-topbar__num">
          SCENARIO {scenarioNumber} / {totalScenarios}
        </span>
        <span className="scenario-topbar__name">— {scenario.short_label}</span>
      </div>
      <div className="scenario-topbar__right">
        <span className="scenario-topbar__timer" aria-label="Session time">
          <span className="scenario-topbar__pulse" aria-hidden />
          {formatTime(elapsed)}
        </span>
        <button type="button" className="scenario-topbar__end" onClick={onEndSession}>
          End session
        </button>
      </div>
    </header>
  );
}

/* ------------------------------ Brief strip --------------------------- */

function BriefStrip({ scenario }: { scenario: ScenarioConfig }) {
  const persona = PERSONAS_SEED[scenario.persona_id];
  const initials = persona.display_name
    .split(' ')
    .map((p) => p[0])
    .join('');
  return (
    <div className={`brief-strip brief-strip--${persona.color_key}`}>
      <div className={`brief-strip__avatar brief-strip__avatar--${persona.color_key}`} aria-hidden>
        {initials}
      </div>
      <div className="brief-strip__meta">
        <p className="brief-strip__name">
          {persona.display_name}
          <span className="brief-strip__role"> · {persona.role_line}</span>
        </p>
        <p className="brief-strip__context">{scenario.context_line}</p>
      </div>
      <div className="brief-strip__tags">
        {scenario.mood_tags.map((tag) => (
          <span key={tag} className="brief-strip__tag">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- Temperature gauge ------------------------- */

function TemperatureGauge({
  fraction,
  label,
  colorKey,
  personaName,
  pronouns,
}: {
  fraction: number;
  label: string;
  colorKey: 'warmth' | 'growth' | 'danger';
  personaName: string;
  pronouns: import('@/seed/personas').PersonaPronouns;
}) {
  // Briefly flash on value change so the learner notices the gauge moved
  // when a question landed (or didn't). Driven off `fraction` rather than a
  // dedicated "delta" prop so we capture mock and live updates uniformly.
  const [pulse, setPulse] = useState(false);
  const prevFractionRef = useRef(fraction);
  useEffect(() => {
    if (Math.abs(fraction - prevFractionRef.current) < 0.001) return;
    prevFractionRef.current = fraction;
    setPulse(true);
    const id = window.setTimeout(() => setPulse(false), 650);
    return () => window.clearTimeout(id);
  }, [fraction]);

  return (
    <div
      className={`temp-gauge ${pulse ? 'temp-gauge--pulse' : ''}`}
      role="status"
      aria-label={`${personaName}'s read on you: ${label}`}
    >
      <div className="temp-gauge__head">
        <span className={`temp-gauge__icon temp-gauge__icon--${colorKey}`} aria-hidden>
          <ThermometerIcon />
        </span>
        <p className="temp-gauge__title">
          <span className="temp-gauge__title-name">{personaName}</span>
          <span className="temp-gauge__title-rest">'s read on you</span>
        </p>
        <p className="temp-gauge__hint">
          Specific, operational questions warm {pronouns.object} up. Vague pitches cool{' '}
          {pronouns.object} off.
        </p>
      </div>
      <div className="temp-gauge__row">
        <div className="temp-gauge__track" aria-hidden>
          {[0, 1, 2, 3].map((i) => {
            const segFrac = Math.max(0, Math.min(1, fraction * 4 - i));
            return (
              <div key={i} className="temp-gauge__seg">
                <div
                  className={`temp-gauge__fill temp-gauge__fill--${colorKey}`}
                  style={{ width: `${segFrac * 100}%` }}
                />
              </div>
            );
          })}
        </div>
        <span className={`temp-gauge__label temp-gauge__label--${colorKey}`}>{label}</span>
      </div>
    </div>
  );
}

function ThermometerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 14.76V4a2 2 0 0 0-4 0v10.76a4 4 0 1 0 4 0z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="17.5" r="1.5" fill="currentColor" />
    </svg>
  );
}

/* ---------------------------- Persona stage --------------------------- */

interface PersonaStageProps {
  scenario: ScenarioConfig;
  sceneState: SceneState;
  colorKey: 'warmth' | 'growth' | 'danger';
  startData: ReturnType<typeof buildRoleplayStartData>;
  onReady: () => void;
  onResponse: (r: CBResponse) => void;
  onSegmentEnded: () => void;
  micProps: {
    onMicrophoneData: (d: { text: string; isFinal: boolean }) => void;
    onMicrophoneListening: () => void;
    onMicrophoneStopped: () => void;
  };
  onPersonaSignals: (s: import('@/lib/codebaby').CBPersonaSignals) => void;
}

const PersonaStage = forwardRef<AvatarHandle, PersonaStageProps>(function PersonaStage(
  {
    scenario,
    sceneState,
    colorKey,
    startData,
    onReady,
    onResponse,
    onSegmentEnded,
    micProps,
    onPersonaSignals,
  },
  ref,
) {
  const persona = PERSONAS_SEED[scenario.persona_id];
  const firstName = persona.display_name.split(' ')[0];
  const pillText =
    sceneState === 'speaking'
      ? `${firstName} is speaking`
      : sceneState === 'you-speaking'
        ? "You're speaking"
        : `${firstName} is waiting for your response`;
  return (
    <div className={`persona-stage persona-stage--${colorKey}`}>
      <span className={`persona-stage__pill persona-stage__pill--${sceneState}`}>
        <span className="persona-stage__pill-dot" aria-hidden />
        {pillText}
      </span>
      <div className={`persona-stage__circle persona-stage__circle--${colorKey}`}>
        <AvatarMount
          ref={ref}
          loaderId={loaderIdFor(scenario.persona_id)}
          startData={startData}
          ariaLabel={`${persona.display_name}, buyer persona`}
          onPlayerBuilt={onReady}
          onResponse={onResponse}
          onSegmentEnded={onSegmentEnded}
          {...micProps}
          onPersonaSignals={onPersonaSignals}
        />
      </div>
      <div className="persona-stage__caption">
        <p className="persona-stage__name">{persona.display_name}</p>
        <p className="persona-stage__role">{persona.role_line}</p>
      </div>
    </div>
  );
});

function loaderIdFor(key: PersonaKey): string {
  // Generic lookup — every PersonaKey is an AvatarKey, so no per-persona cases.
  return AVATAR_IDS[key];
}

/* ---------------------------- Transcript rail ------------------------- */

function TranscriptRail({
  transcript,
  personaName,
  sceneState,
}: {
  transcript: TranscriptTurn[];
  personaName: string;
  sceneState: SceneState;
}) {
  // Show the full conversation. Container scrolls internally and auto-pins
  // to the bottom on every new turn so the most recent line is always in
  // view without forcing the page itself to grow (which previously pushed
  // the input dock below the fold).
  const lastPersonaIdx = lastIndexOf(transcript, (t) => t.speaker === 'persona');
  const firstName = personaName.split(' ')[0];
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [transcript]);

  return (
    <aside className="transcript">
      <p className="transcript__eyebrow">Live transcript</p>
      {transcript.length === 0 ? (
        <div className="transcript__empty">
          Your conversation with {firstName} starts when you hit Start session.
        </div>
      ) : (
        <ol ref={listRef} className="transcript__list">
          {transcript.map((turn, i) => {
            const isLearner = turn.speaker === 'learner';
            const isLatestBuyer = !isLearner && i === lastPersonaIdx;
            return (
              <li
                key={`${turn.at_s}-${i}`}
                className={[
                  'transcript__turn',
                  isLearner ? 'transcript__turn--you' : 'transcript__turn--persona',
                  isLatestBuyer && 'transcript__turn--latest',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="transcript__who">{isLearner ? 'You' : firstName}</span>
                <p className="transcript__text">{turn.text}</p>
              </li>
            );
          })}
        </ol>
      )}
      {sceneState === 'waiting' && transcript.length > 0 && (
        <div className="transcript__your-turn">
          <span className="transcript__your-turn-pill">Your turn</span>
        </div>
      )}
    </aside>
  );
}

function lastIndexOf<T>(arr: T[], pred: (t: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) if (pred(arr[i]!)) return i;
  return -1;
}

/* ------------------------------ Controls ------------------------------ */

function ControlsRow({
  value,
  onChange,
  onSend,
  onMicTap,
  micState,
  continuous = false,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onMicTap: () => void;
  micState: MicState;
  continuous?: boolean;
  disabled: boolean;
}) {
  const placeholder = disabled
    ? 'Start the session to begin speaking'
    : continuous
      ? 'Listening — just talk. You can also type here.'
      : micState === 'listening'
        ? 'Listening… speak naturally, then tap the mic to stop.'
        : micState === 'committing'
          ? 'Sending…'
          : 'Type your next line — or tap the mic.';

  return (
    <form
      className="controls"
      onSubmit={(e) => {
        e.preventDefault();
        if (disabled) return;
        onSend();
      }}
    >
      <MicButton
        state={micState}
        onTap={onMicTap}
        disabled={disabled}
        size="md"
        continuous={continuous}
      />
      <input
        type="text"
        className="controls__input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      <button type="submit" className="controls__send" disabled={disabled || !value.trim()}>
        Send <ArrowRight />
      </button>
    </form>
  );
}

function SecondaryRow({ onAskHint, hintPrimed }: { onAskHint: () => void; hintPrimed: boolean }) {
  return (
    <div className="secondary-row">
      <button
        type="button"
        className="secondary-row__hint"
        onClick={onAskHint}
        disabled={!hintPrimed}
      >
        <span className="secondary-row__hint-dot" aria-hidden />
        Ask {COACH.firstName} for a hint
      </button>
      <span className="secondary-row__note">Mic tap-to-toggle · text always on</span>
    </div>
  );
}

/* --------------------------- Paused hint card ------------------------- */

function PausedHintCard({
  hint,
  personaName,
  onResume,
}: {
  hint: HintPayload;
  personaName: string;
  onResume: () => void;
}) {
  const firstName = personaName.split(' ')[0];
  return (
    <>
      <div className="paused-backdrop" aria-hidden />
      <aside className="paused-hint" role="dialog" aria-labelledby="paused-hint-title">
        <p className="paused-hint__eyebrow">
          {COACH.firstName} · <span className="paused-hint__tag">{hint.tag}</span>
        </p>
        <p className="paused-hint__text" id="paused-hint-title">
          {hint.text}
        </p>
        <button type="button" className="paused-hint__resume" onClick={onResume}>
          Resume session <ArrowRight />
        </button>
        <p className="paused-hint__note">
          Your conversation with {firstName} is paused. Nothing here is added to the transcript.
        </p>
      </aside>
    </>
  );
}

/* -------------------------------- Icons ------------------------------- */

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
/* ------------------------------ Utilities ----------------------------- */

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
